import React, { useEffect, useState } from "react";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/CouponSlice";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import CouponsListing from "./CouponsListing";
import {
  useChangeStatusCouponByIdMutation,
  useDeleteCouponByIdMutation,
  useGetAllCouponDataQuery,
} from "src/services/CouponService";
import AddCouponWrapper from "../Add/AddCouponWrapper";
import EditCouponWrapper from "../Edit/EditCouponWrapper";
import { capitalizeFirstLetter } from "src/utils/helper/stringHelper";
import { usePermission } from "src/hooks/usePermission";

type Props = {};

const CouponsListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    isOpenAddDialog,
    isOpenEditDialog,
    searchValue,
    isTableLoading,
  } = useSelector((state: RootState) => state.coupon);
  const [selectedCouponId, setSelectedCouponId] = useState<string>();
  const [deleteCoupon] = useDeleteCouponByIdMutation();
  const [changeStatusCoupon] = useChangeStatusCouponByIdMutation();
  const { data, isLoading, isFetching } = useGetAllCouponDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["couponCode"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  // Setting Module Data
  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const handleDeleteCoupon = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this Coupon ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteCoupon(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusCoupon = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this Coupon ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusCoupon({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const moduleName = "COUPON";

  const canEdit = usePermission(moduleName, "UPDATE");
  const canDelete = usePermission(moduleName, "DELETE");
  const canActive = usePermission(moduleName, "ACTIVE/DEACTIVE");

  const columns: columnTypes[] = [
    {
      field: "couponCode",
      headerName: "Coupon Code",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "minOrderAmount",
      headerName: "Min Order Amount",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "discountType",
      headerName: "Discount Type",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <span>{capitalizeFirstLetter(row.discountType)}</span>
      ),
    },
    {
      field: "discountValue",
      headerName: "Discount Value",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "startsDate",
      headerName: "Start Date",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "expiryDate",
      headerName: "Expiry Date",
      flex: "flex-[1_1_0%]",
    },
    // {
    //   field: "product",
    //   headerName: "Products",
    //   flex: "flex-[1_1_0%]",
    // },
    {
      field: "maxCount",
      headerName: "Max Count",
      flex: "flex-[1_1_0%]",
    },
    // {
    //   field: "student",
    //   headerName: "Student",
    //   flex: "flex-[1_1_0%]",
    // },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      align: "center",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = [];

        if (canEdit) {
          options.push({
            label: "Edit",
            onClick: () => {
              dispatch(setIsOpenEditDialog(true));
              setSelectedCouponId(row?._id);
            },
          });
        }

        if (canActive) {
          options.push({
            label: "Active/Deactive",
            onClick: () => {
              handleChangeStatusCoupon(row?._id);
            },
          });
        }

        if (canDelete) {
          options.push({
            label: "Delete",
            onClick: () => {
              handleDeleteCoupon(row?._id);
            },
          });
        }

        return <ATMMenu options={options} />;
      },
    },
  ];

  return (
    <>
      <div>
        <CouponsListing
          columns={columns}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setIsOpenAddDialog: (value) => dispatch(setIsOpenAddDialog(value)),
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenAddDialog ? (
        <AddCouponWrapper onClose={() => dispatch(setIsOpenAddDialog(false))} />
      ) : null}
      {isOpenEditDialog ? (
        <EditCouponWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedCouponId={selectedCouponId || ""}
        />
      ) : null}
    </>
  );
};

export default CouponsListingWrapper;
